import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, Input, InputNumber, Select } from 'antd';
import { message } from '../../utils/antd-message';
import { DraggableModal } from '../../components/DraggableModal';
import {
  billingApi,
  maskOrderMobile,
} from '../../api/billing.api';
import {
  productApi,
  type ProductItem,
} from '../../api/products.api';
import {
  studentsApi,
  type StudentListItem,
} from '../../api/students.api';
import { orgCampusApi, type CampusItem } from '../../api/campuses.api';
import { sortCampusesByNumericCode } from '../../utils/campus-sort';
import type { FormInstance } from 'antd';
import { MoneyDisplay } from '../../components/MoneyDisplay';
import { useHasPermission } from '../../auth/HasPermission';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';

interface OrderCreateFormValues {
  studentId: string;
  productId: string;
  /** 减免金额（元，两位小数；仅 DISCOUNT 授权可见，manual-order-discount）。 */
  discountAmountYuan?: number;
  /** 减免原因（减免大于 0 时必填，≤200 字）。 */
  discountReason?: string;
}

export interface OrderCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/** 管理端直建订单对话框。 */
export function OrderCreateModal({ open, onClose, onCreated }: OrderCreateModalProps) {
  const [form] = Form.useForm<OrderCreateFormValues>();
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [studentSearching, setStudentSearching] = useState(false);
  // 已选学生留存：远程联想刷新选项后防选中项显示裸 UUID（LedgerPage 同款回显口径）
  const [pickedStudent, setPickedStudent] = useState<StudentListItem | null>(null);
  const studentSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const canDiscount = useHasPermission('ADM-09', 'DISCOUNT');

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setStudents([]);
    setPickedStudent(null);
    setProducts([]);
    Promise.all([
      productApi.listProducts({ status: 'ON_SHELF', page: 1, pageSize: 100 }),
      orgCampusApi.listCampuses({ status: 'ACTIVE', page: 1, pageSize: 100 }),
    ])
      .then(([productResult, campusResult]) => {
        setProducts(productResult.items);
        setCampuses(sortCampusesByNumericCode(campusResult.items));
      })
      .catch((err: unknown) => {
        message.error(err instanceof Error ? err.message : '基础数据加载失败');
      });
  }, [form, open]);

  // 卸载时清掉未触发的防抖定时器，避免关窗后回调打接口
  useEffect(() => {
    return () => {
      if (studentSearchTimer.current) clearTimeout(studentSearchTimer.current);
    };
  }, []);

  // O-28：学生联想随全局校区（全部校区不带参）
  const { scope } = useGlobalCampusScope();
  // 学生远程联想（BS-018：不再整表拉前 100 条静默截断，服务端按姓名/学号/手机号过滤，300ms 防抖）
  const handleStudentSearch = (keyword: string) => {
    if (studentSearchTimer.current) clearTimeout(studentSearchTimer.current);
    if (!keyword.trim()) {
      setStudents([]);
      return;
    }
    studentSearchTimer.current = setTimeout(() => {
      void (async () => {
        setStudentSearching(true);
        try {
          const result = await studentsApi.listStudents({
            keyword: keyword.trim(),
            // O-28：学生联想随全局校区（全部校区不带参）
            campus: scope === 'ALL' ? undefined : scope,
            page: 1,
            pageSize: 20,
          });
          setStudents(result.items);
        } catch {
          setStudents([]);
        } finally {
          setStudentSearching(false);
        }
      })();
    }, 300);
  };

  const handleOk = async () => {
    let values: OrderCreateFormValues;
    try {
      values = await form.validateFields();
    } catch {
      // 表单校验失败：错误已内联展示，不弹「创建订单失败」
      return;
    }
    // useWatch 下沉内层组件后，提交侧经表单值解析学生（置顶回显优先，防远程刷新丢选项）
    const studentId = values.studentId;
    const selectedStudent =
      (pickedStudent && pickedStudent.id === studentId ? pickedStudent : null) ??
      students.find((student) => student.id === studentId) ??
      null;
    if (!selectedStudent) {
      message.error('请选择学生');
      return;
    }
    // manual-order-discount SC-05：应收联动 = 定价 − 减免；元转分整数安全转换（SC-06）
    const discountMinor = canDiscount ? Math.round((values.discountAmountYuan ?? 0) * 100) : 0;
    setLoading(true);
    try {
      // manual-order-discount SC-02：未授权会话不携带减免字段；服务端 DISCOUNT 校验为最终防线
      const result = await billingApi.createDirectOrder({
        student: {
          studentId: selectedStudent.id,
          campusId: selectedStudent.homeCampusId,
        },
        productId: values.productId,
        ...(canDiscount
          ? {
              discountAmount: discountMinor,
              discountReason:
                discountMinor > 0 ? (values.discountReason ?? '').trim() : null,
            }
          : {}),
      });
      message.success(`订单 ${result.orderNo} 已创建`);
      onCreated();
      onClose();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DraggableModal
      open={open}
      title="创建订单"
      okText="创建"
      cancelText="取消"
      confirmLoading={loading}
      onOk={handleOk}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <OrderCreateFormFields
          form={form}
          students={students}
          products={products}
          campuses={campuses}
          pickedStudent={pickedStudent}
          studentSearching={studentSearching}
          canDiscount={canDiscount}
          onPickStudent={setPickedStudent}
          onStudentSearch={handleStudentSearch}
        />
      </Form>
    </DraggableModal>
  );
}

/** 表单字段内层组件：useWatch 与派生金额/警示区随 Form 仅开窗挂载（Modal 带
 *  destroyOnHidden 而组件常驻父页面），消除关窗态「useForm not connected」
 *  开发告警；提交侧取值改经 form.validateFields 值解析（父组件 handleOk）。 */
interface OrderCreateFormFieldsProps {
  form: FormInstance<OrderCreateFormValues>;
  students: StudentListItem[];
  products: ProductItem[];
  campuses: CampusItem[];
  pickedStudent: StudentListItem | null;
  studentSearching: boolean;
  canDiscount: boolean;
  onPickStudent: (student: StudentListItem | null) => void;
  onStudentSearch: (keyword: string) => void;
}

function OrderCreateFormFields({
  form,
  students,
  products,
  campuses,
  pickedStudent,
  studentSearching,
  canDiscount,
  onPickStudent,
  onStudentSearch,
}: OrderCreateFormFieldsProps) {
  const studentId = Form.useWatch('studentId', form);
  const productId = Form.useWatch('productId', form);
  const discountYuan = Form.useWatch('discountAmountYuan', form);
  const selectedStudent = pickedStudent && pickedStudent.id === studentId ? pickedStudent : null;
  const selectedProduct = useMemo(
    () => products.find((product) => product.id === productId),
    [productId, products],
  );
  // manual-order-discount SC-05：应收联动 = 定价 − 减免；未授权/未填减免时恒为定价；
  // 元转分用整数安全转换（Math.round 抹平浮点误差，19.9 元 → 1990 分，SC-06）
  const discountMinor = canDiscount ? Math.round((discountYuan ?? 0) * 100) : 0;
  const receivableMinor = selectedProduct
    ? Math.max(0, selectedProduct.price - discountMinor)
    : 0;

  return (
    <>
      <Form.Item
        name="studentId"
        label="学生"
        rules={[{ required: true, message: '请选择学生' }]}
      >
        <Select
          showSearch
          filterOption={false}
          onSearch={onStudentSearch}
          loading={studentSearching}
          notFoundContent="输入姓名/学号/手机号搜索学生"
          placeholder="输入姓名/学号/手机号搜索学生"
          options={(() => {
            // 选项文案：姓名 · 脱敏手机号 · 归属校区（与原全量口径一致）
            const labelOf = (student: StudentListItem) =>
              `${student.name} · ${maskOrderMobile(student.guardianMobile)} · ${student.homeCampusName ?? ''}`;
            const mapped = students.map((student) => ({
              value: student.id,
              label: labelOf(student),
            }));
            if (!selectedStudent) return mapped;
            const pinned = { value: selectedStudent.id, label: labelOf(selectedStudent) };
            return [pinned, ...mapped.filter((option) => option.value !== pinned.value)];
          })()}
          onChange={(value: string) => {
            onPickStudent(students.find((student) => student.id === value) ?? null);
          }}
          data-testid="order-create-student-select"
        />
      </Form.Item>
      <Form.Item
        name="productId"
        label="课程商品包"
        rules={[{ required: true, message: '请选择在售课程商品包' }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="选择在售课程商品包"
          options={products.map((product) => ({
            value: product.id,
            label: `${product.name} · ¥${(product.price / 100).toFixed(2)}`,
          }))}
          data-testid="order-create-product-select"
        />
      </Form.Item>
      {selectedStudent && (
        <Form.Item label="归属校区">
          <Select
            disabled
            value={selectedStudent.homeCampusId}
            options={campuses.map((c) => ({ label: c.name, value: c.id }))}
          />
        </Form.Item>
      )}
      {canDiscount && selectedProduct && (
        <>
          <Form.Item
            name="discountAmountYuan"
            label="减免金额（元）"
            rules={[
              {
                validator: (_rule, value: number | undefined | null) => {
                  if (value === undefined || value === null || value === 0) {
                    return Promise.resolve();
                  }
                  if (value < 0) return Promise.reject(new Error('减免金额不能为负'));
                  if (selectedProduct && Math.round(value * 100) > selectedProduct.price) {
                    return Promise.reject(new Error('减免金额不能超过商品原始价格'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            {/* 不设 max：antd InputNumber 失焦会把超限值静默钳制到上限，
                恰全额减免会造成误建 0 元单；超限交给下方 validator 明确报错拦截 */}
            <InputNumber
              min={0}
              precision={2}
              style={{ width: 200 }}
              placeholder="0 表示不减免"
              data-testid="order-create-discount-input"
            />
          </Form.Item>
          <Form.Item
            name="discountReason"
            label="减免原因"
            rules={[
              {
                validator: (_rule, value: string | undefined) => {
                  if (discountMinor > 0 && !(value ?? '').trim()) {
                    return Promise.reject(new Error('减免大于 0 时必须填写原因'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.TextArea
              rows={2}
              maxLength={200}
              placeholder="减免大于 0 时必填"
              data-testid="order-create-discount-reason"
            />
          </Form.Item>
        </>
      )}
      {selectedProduct && (
        <Alert
          type="info"
          showIcon
          message={
            canDiscount && discountMinor > 0
              ? <>原价 <MoneyDisplay amountInCents={selectedProduct.price} /> − 减免 <MoneyDisplay amountInCents={discountMinor} /> = 应收总额 <MoneyDisplay amountInCents={receivableMinor} /></>
              : <>应收总额 <MoneyDisplay amountInCents={selectedProduct.price} /></>
          }
          description={
            <>
              {/* period-pass-admin-ui:SC-05：所选商品含期限卡 SKU 行时追加结清开通提示 */}
              {selectedProduct.skuConfigs.some((c) => c.chargeMode === 'PERIOD') && (
                <div data-testid="order-create-period-tip">
                  含期限卡 SKU：结清后创建待激活期限卡，首次上课自动生效，有效期内不限次上课（不按课时开通）。
                </div>
              )}
              <span>
                {`首付款 ${(selectedProduct.depositRatio * 100).toFixed(0)}%；商品包含 ${selectedProduct.skuConfigs.length} 个 SKU，整包购买。`}
              </span>
            </>
          }
          data-testid="order-create-amount-alert"
        />
      )}
    </>
  );
}
